import { useEffect, useState, useMemo } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import {
  SignOut,
  DownloadSimple,
  Trash,
  MagnifyingGlass,
  Users,
  Envelope,
  ClockCounterClockwise,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { adminApi, getToken, clearToken, formatApiError, API } from "../lib/adminApi";

const formatDate = (iso) => {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
};

export default function AdminWaitlist() {
  const navigate = useNavigate();
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [adminEmail, setAdminEmail] = useState("");

  useEffect(() => {
    if (!getToken()) return;
    const load = async () => {
      try {
        setLoading(true);
        const [me, list] = await Promise.all([
          adminApi.get("/auth/me"),
          adminApi.get("/admin/waitlist"),
        ]);
        setAdminEmail(me.data.email);
        setEntries(list.data);
      } catch (err) {
        if (err?.response?.status === 401) {
          clearToken();
          navigate("/admin", { replace: true });
          return;
        }
        toast.error(formatApiError(err));
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [navigate]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return entries;
    return entries.filter((e) => e.email.toLowerCase().includes(q));
  }, [entries, query]);

  const todayCount = useMemo(() => {
    const today = new Date();
    return entries.filter((e) => {
      try {
        const d = new Date(e.created_at);
        return (
          d.getFullYear() === today.getFullYear() &&
          d.getMonth() === today.getMonth() &&
          d.getDate() === today.getDate()
        );
      } catch {
        return false;
      }
    }).length;
  }, [entries]);

  if (!getToken()) return <Navigate to="/admin" replace />;

  const onLogout = () => {
    clearToken();
    toast.success("Signed out.");
    navigate("/admin", { replace: true });
  };

  const onDelete = async (id) => {
    if (!window.confirm("Remove this entry from the waitlist?")) return;
    try {
      await adminApi.delete(`/admin/waitlist/${id}`);
      setEntries((prev) => prev.filter((e) => e.id !== id));
      toast.success("Entry removed.");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const onExport = async () => {
    try {
      const res = await fetch(`${API}/admin/waitlist/export`, {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      if (!res.ok) throw new Error(`Export failed (${res.status})`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const ts = new Date()
        .toISOString()
        .replace(/[:.]/g, "-")
        .slice(0, 19);
      a.download = `kim-monroe-waitlist-${ts}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("CSV exported.");
    } catch (err) {
      toast.error(err.message || "Export failed.");
    }
  };

  return (
    <main
      data-testid="admin-waitlist-page"
      className="min-h-screen bg-[#0a0705] text-[#FDFBF7]"
    >
      {/* Top bar */}
      <header className="border-b border-white/10 bg-black/40 backdrop-blur-xl sticky top-0 z-20">
        <div className="max-w-7xl mx-auto px-6 sm:px-10 h-20 flex items-center justify-between">
          <a href="/" className="flex items-center gap-4">
            <img
              src="/logo-wordmark.png"
              alt="Kim Monroe LLC"
              className="h-10 w-auto"
            />
            <span className="hidden sm:inline text-xs uppercase tracking-[0.3em] text-[#A39791] font-body border-l border-white/10 pl-4">
              Atelier Dashboard
            </span>
          </a>
          <div className="flex items-center gap-5">
            <span
              data-testid="admin-email"
              className="hidden sm:inline text-xs text-[#A39791] font-body"
            >
              {adminEmail}
            </span>
            <button
              onClick={onLogout}
              data-testid="admin-logout-button"
              className="flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-[#A39791] hover:text-[#FDFBF7] transition-colors"
            >
              <SignOut size={16} weight="thin" />
              Sign out
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 sm:px-10 py-12">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-10">
          <div>
            <div className="flex items-center gap-3 mb-4">
              <span className="h-px w-10 bg-[#E6CC98]" />
              <span className="text-xs uppercase tracking-[0.3em] gold-text font-body">
                Waitlist
              </span>
            </div>
            <h1 className="font-heading font-light text-4xl sm:text-5xl tracking-tight leading-tight">
              Private access, managed.
            </h1>
            <p className="mt-3 text-sm text-[#A39791] font-body max-w-xl">
              Every email captured from the landing page lives here. Export,
              remove, or filter at will.
            </p>
          </div>
          <button
            onClick={onExport}
            data-testid="admin-export-csv"
            className="btn-gold px-6 py-3.5 text-xs uppercase tracking-[0.3em] flex items-center gap-2 self-start md:self-auto"
          >
            <DownloadSimple size={16} weight="bold" />
            Export CSV
          </button>
        </div>

        {/* Stats */}
        <div
          data-testid="admin-stats"
          className="grid grid-cols-1 sm:grid-cols-3 gap-px bg-white/5 border border-white/5 mb-10"
        >
          {[
            { icon: Users, label: "Total members", value: entries.length },
            { icon: ClockCounterClockwise, label: "Joined today", value: todayCount },
            {
              icon: Envelope,
              label: "Unique domains",
              value: new Set(
                entries.map((e) => (e.email.split("@")[1] || "").toLowerCase())
              ).size,
            },
          ].map((s) => (
            <div
              key={s.label}
              className="bg-[#140E0B] p-6 sm:p-8"
              data-testid={`admin-stat-${s.label.toLowerCase().replace(/\s/g, "-")}`}
            >
              <s.icon size={20} weight="thin" className="text-[#E6CC98]" />
              <div className="font-heading font-light text-4xl mt-4 gold-gradient-text">
                {s.value}
              </div>
              <div className="text-[10px] uppercase tracking-[0.25em] text-[#A39791] font-body mt-1">
                {s.label}
              </div>
            </div>
          ))}
        </div>

        {/* Search */}
        <div className="relative mb-6 max-w-md">
          <MagnifyingGlass
            size={16}
            weight="thin"
            className="absolute left-4 top-1/2 -translate-y-1/2 text-[#A39791]"
          />
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search by email..."
            data-testid="admin-search-input"
            className="glass-input w-full pl-11 pr-4 py-3 text-sm font-body"
          />
        </div>

        {/* Table */}
        <div
          data-testid="admin-waitlist-table"
          className="border border-white/5 bg-[#140E0B]/60"
        >
          <div className="hidden md:grid grid-cols-12 gap-4 px-6 py-4 border-b border-white/5 text-[10px] uppercase tracking-[0.25em] text-[#A39791] font-body">
            <div className="col-span-6">Email</div>
            <div className="col-span-2">Source</div>
            <div className="col-span-3">Joined</div>
            <div className="col-span-1 text-right">—</div>
          </div>

          {loading ? (
            <div className="px-6 py-12 text-center text-sm text-[#A39791] font-body">
              Loading waitlist...
            </div>
          ) : filtered.length === 0 ? (
            <div
              data-testid="admin-empty-state"
              className="px-6 py-16 text-center text-sm text-[#A39791] font-body"
            >
              {entries.length === 0
                ? "No one's on the list yet. Share the landing page."
                : "No matches for your search."}
            </div>
          ) : (
            <ul>
              {filtered.map((e) => (
                <li
                  key={e.id}
                  data-testid={`admin-entry-${e.id}`}
                  className="grid grid-cols-1 md:grid-cols-12 gap-2 md:gap-4 px-6 py-5 border-b border-white/5 hover:bg-white/[0.02] transition-colors items-center"
                >
                  <div className="md:col-span-6 font-body text-sm text-[#FDFBF7] break-all">
                    {e.email}
                  </div>
                  <div className="md:col-span-2 text-xs uppercase tracking-[0.2em] text-[#A39791] font-body">
                    {e.source || "landing_page"}
                  </div>
                  <div className="md:col-span-3 text-xs text-[#A39791] font-body">
                    {formatDate(e.created_at)}
                  </div>
                  <div className="md:col-span-1 flex md:justify-end">
                    <button
                      onClick={() => onDelete(e.id)}
                      data-testid={`admin-delete-${e.id}`}
                      aria-label="Delete entry"
                      className="w-9 h-9 border border-white/10 flex items-center justify-center text-[#A39791] hover:border-red-400/40 hover:text-red-300 transition-colors"
                    >
                      <Trash size={14} weight="thin" />
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        <p className="mt-6 text-xs text-[#A39791]/70 font-body uppercase tracking-[0.25em]">
          {filtered.length} of {entries.length} shown
        </p>
      </div>
    </main>
  );
}
